/**
 * 项目管理系统 — 前后端共享类型定义
 * 前端和后端 Agent 均应参考此文件定义数据类型
 */

// ==================== 枚举 ====================

export enum ProjectStatus {
  IN_PROGRESS = 'IN_PROGRESS',
  COMPLETED = 'COMPLETED',
  ARCHIVED = 'ARCHIVED',
  GATE_BLOCKED = 'GATE_BLOCKED',
}

export enum ProjectStageStatus {
  PENDING = 'PENDING',
  IN_PROGRESS = 'IN_PROGRESS',
  COMPLETED = 'COMPLETED',
}

export enum TaskStatus {
  NOT_STARTED = 'NOT_STARTED',
  IN_PROGRESS = 'IN_PROGRESS',
  COMPLETED = 'COMPLETED',
  OVERDUE = 'OVERDUE',
}

export enum TaskPriority {
  HIGH = 'HIGH',
  MEDIUM = 'MEDIUM',
  LOW = 'LOW',
}

export enum TaskType {
  FEATURE = 'FEATURE',
  BUG = 'BUG',
  INITIATION = 'INITIATION',
}

export enum UserStatus {
  ACTIVE = 'ACTIVE',
  DISABLED = 'DISABLED',
}

export enum ProjectType {
  STANDARD = 'STANDARD',
  CARD = 'CARD',
  PRODUCT = 'PRODUCT',
}

export enum RequirementStatus {
  PENDING_ASSIGN = 'PENDING_ASSIGN',
  PENDING_RESPONSE = 'PENDING_RESPONSE',
  IN_PROGRESS = 'IN_PROGRESS',
  PENDING_TEST_RESPONSE = 'PENDING_TEST_RESPONSE',
  TESTING = 'TESTING',
  PENDING_ACCEPTANCE_RESPONSE = 'PENDING_ACCEPTANCE_RESPONSE',
  ACCEPTANCE = 'ACCEPTANCE',
  COMMUNICATING = 'COMMUNICATING',
  COMPLETED = 'COMPLETED',
  CLOSED = 'CLOSED',
  PAUSED = 'PAUSED',
}

export enum DeviceType {
  WIRED = 'WIRED',
  WIRELESS = 'WIRELESS',
  CHARGING = 'CHARGING',
}

export enum ResponseType {
  ACCEPT = 'ACCEPT',
  ACCEPT_WITH_CONDITIONS = 'ACCEPT_WITH_CONDITIONS',
  REJECT = 'REJECT',
}

export enum RequirementResponseStage {
  DEV = 'DEV',
  TEST = 'TEST',
  ACCEPTANCE = 'ACCEPTANCE',
}

export enum RequirementResponseAction {
  ACCEPT = 'ACCEPT',
  ACCEPT_WITH_EXTENSION = 'ACCEPT_WITH_EXTENSION',
  REJECT = 'REJECT',
  ROLLBACK = 'ROLLBACK',
}

export enum RequirementEventType {
  REQ_OVERDUE = 'REQ_OVERDUE',
}

export enum RoleName {
  ADMIN = 'admin',
  PROJECT_MANAGER = 'project_manager',
  MEMBER = 'member',
}

export enum PermType {
  MENU = 'MENU',
  BUTTON = 'BUTTON',
  DATA = 'DATA',
}

export enum DataScope {
  ALL = 'ALL',
  DEPARTMENT = 'DEPARTMENT',
  SELF = 'SELF',
}

export interface PermissionItem {
  id: string;
  code: string;
  name: string;
  type: PermType;
  parentId?: string;
  path?: string;
  icon?: string;
  component?: string;
  sortOrder: number;
  status: number;
  description?: string;
  children?: PermissionItem[];
}

export enum Permission {
  // 项目管理
  PROJECT_VIEW = 'PROJECT_VIEW',
  PROJECT_CREATE = 'PROJECT_CREATE',
  PROJECT_UPDATE = 'PROJECT_UPDATE',
  PROJECT_DELETE = 'PROJECT_DELETE',
  PROJECT_ARCHIVE = 'PROJECT_ARCHIVE',
  PROJECT_EXPORT = 'PROJECT_EXPORT',

  // 任务管理
  TASK_VIEW = 'TASK_VIEW',
  TASK_CREATE = 'TASK_CREATE',
  TASK_UPDATE = 'TASK_UPDATE',
  TASK_DELETE = 'TASK_DELETE',
  TASK_ASSIGN = 'TASK_ASSIGN',

  // 部门管理
  DEPT_VIEW = 'DEPT_VIEW',
  DEPT_MANAGE = 'DEPT_MANAGE',

  // 用户与角色管理
  USER_VIEW = 'USER_VIEW',
  USER_MANAGE = 'USER_MANAGE',
  ROLE_VIEW = 'ROLE_VIEW',
  ROLE_MANAGE = 'ROLE_MANAGE',

  // 统计分析
  STAT_VIEW = 'STAT_VIEW',

  // 需求管理
  REQ_VIEW = 'REQ_VIEW',
  REQ_CREATE = 'REQ_CREATE',
  REQ_UPDATE = 'REQ_UPDATE',
  REQ_DELETE = 'REQ_DELETE',
  REQ_ASSIGN = 'REQ_ASSIGN',

  // 产品立项
  PRODUCT_INIT_VIEW = 'PRODUCT_INIT_VIEW',
  PRODUCT_INIT_CREATE = 'PRODUCT_INIT_CREATE',
  PRODUCT_GATE_APPROVE = 'PRODUCT_GATE_APPROVE',
}

// ==================== 基础实体 ====================

export interface Department {
  id: string;
  name: string;
  description?: string;
  managerId?: string;
  managerName?: string;
  sortOrder: number;
  managers?: Array<
    Pick<User, 'id' | 'name'> & { roleName?: string; roleLabel?: string }
  >;
  members?: Array<
    Pick<User, 'id' | 'name' | 'avatar' | 'avatarColor'> & {
      roleName?: string | null;
      roleLabel?: string | null;
      isManager?: boolean;
    }
  >;
  memberCount?: number;
  createdAt: string;
}

export interface Role {
  id: string;
  name: RoleName;
  label: string;
  permissions: string[];
  createdAt: string;
}

export interface User {
  id: string;
  name: string;
  username: string;
  wechatUserId?: string;
  department: Pick<Department, 'id' | 'name'>;
  role: Pick<Role, 'id' | 'name' | 'permissions'>;
  status: UserStatus;
  avatar?: string;
  /** 头像渐变色 key（DefaultAvatar 预设之一），为空时按 userId 哈希默认色 */
  avatarColor?: string;
  email?: string;
  createdAt: string;
}

export interface Stage {
  id: string;
  name: string;
  orderIndex: number;
  color?: string;
  isSystemDefault: boolean;
  createdAt: string;
}

// ==================== 核心业务实体 ====================

export interface ProjectStage {
  id: string;
  stageId?: string;
  name: string;
  orderIndex: number;
  status: ProjectStageStatus;
  startedAt?: string;
  completedAt?: string;
  durationDays?: number;
  plannedEndDate?: string;
  tasks: Task[];
  progress: number; // 0-100
  assignee?: Pick<User, 'id' | 'name'>;
  projectType?: ProjectType;
  stageOrder?: number;
  stageName?: string;
  isGate?: boolean;
  isApprovalStage?: boolean;
  gateApproved?: boolean;
  gateApproverId?: string;
  gateApprovedAt?: string;
  reviews?: any[];
  stageReviews?: any[];
  stageHint?: string;
  fileRequirements?: { category: string; label: string; required: boolean }[];
  conditionalBranches?: {
    condition: string;
    label: string;
    createSubTask: boolean;
  }[];
  branchSelections?: Record<string, boolean>;
  parallelTasksConfig?: { name: string; category: string }[];
  deliverables?: StageDeliverable[];
}

export interface Project {
  id: string;
  name: string;
  description?: string;
  owner: Pick<User, 'id' | 'name'>;
  status: ProjectStatus;
  type?: ProjectType;
  currentStageOrder: number;
  currentStageName?: string;
  currentStageColor?: string;
  progress?: number; // 0-100
  plannedStartDate: string;
  plannedEndDate: string;
  actualStartDate?: string;
  actualEndDate?: string;
  requirementId?: string;
  requirement?: Pick<Requirement, 'id' | 'title'>;
  stages?: ProjectStage[];
  createdAt: string;
  updatedAt: string;
}

export interface Task {
  id: string;
  name: string;
  description?: string;
  projectStageId: string;
  assignee: Pick<User, 'id' | 'name'>;
  department: Pick<Department, 'id' | 'name'>;
  status: TaskStatus;
  priority: TaskPriority;
  type: TaskType;
  parentTaskId?: string;
  plannedStartDate?: string;
  plannedEndDate: string;
  actualStartDate?: string;
  actualEndDate?: string;
  createdAt: string;
  updatedAt: string;
  _count?: {
    subTasks: number;
    comments: number;
    attachments: number;
  };
}

export interface Notification {
  id: string;
  userId: string;
  title: string;
  content: string;
  type: NotificationType;
  isRead: boolean;
  relatedId?: string;
  createdAt: string;
}

export interface OperationLog {
  id: string;
  operator: Pick<User, 'id' | 'name'>;
  action: string;
  targetType: string;
  targetId: string;
  beforeData?: Record<string, any>;
  afterData?: Record<string, any>;
  createdAt: string;
}

// ==================== API 请求类型 ====================

export interface LoginRequest {
  username: string;
  password: string;
}

export interface StageAssignee {
  orderIndex: number;
  assigneeId: string;
}

export interface CustomStage {
  name: string;
  orderIndex: number;
  assigneeId: string;
  durationDays: number;
  stageId?: string;
}

export interface CreateProjectRequest {
  name: string;
  description?: string;
  ownerId: string;
  plannedStartDate: string;
  plannedEndDate: string;
  type?: ProjectType;
  stageAssignees?: StageAssignee[];
  customStages?: CustomStage[];
}

export interface CreateTaskRequest {
  name: string;
  description?: string;
  projectStageId: string;
  assigneeId: string;
  departmentId: string;
  priority?: TaskPriority;
  type?: TaskType;
  parentTaskId?: string;
  plannedStartDate?: string;
  plannedEndDate: string;
}

export interface UpdateTaskStatusRequest {
  status: TaskStatus;
}

export interface CreateUserRequest {
  name: string;
  username: string;
  password: string;
  departmentId: string;
  roleId: string;
  email?: string;
}

// ==================== API 响应类型 ====================

export interface ApiResponse<T> {
  code: number;
  data: T;
  message: string;
}

export interface PaginatedData<T> {
  list: T[];
  total: number;
  page: number;
  pageSize: number;
}

export interface LoginResponse {
  access_token: string;
  user: User;
  permissions?: string[];
  menus?: PermissionItem[];
  dataScopes?: Record<string, string>;
}

export interface ProjectStatistics {
  projectName: string;
  overallProgress: number;
  completedStages: number;
  totalStages: number;
  stageProgress: {
    name: string;
    progress: number;
    taskCount: number;
    completedCount: number;
  }[];
  taskStatusDistribution: Record<TaskStatus, number>;
  overdueTaskCount: number;
}

export interface KanbanColumn {
  id: string;
  title: string;
  orderIndex?: number;
  color?: string;
  status?: string;
  tasks: Task[];
}

export interface KanbanData {
  columns: KanbanColumn[];
}

// ==================== 需求管理 ====================

export interface RequirementResponse {
  id: string;
  requirementId: string;
  responderId: string;
  responder: Pick<User, 'id' | 'name'>;
  type: ResponseType;
  stage?: RequirementResponseStage;
  action?: RequirementResponseAction;
  fromStatus?: RequirementStatus;
  toStatus?: RequirementStatus;
  comment?: string;
  extendDays?: number;
  attachments?: RequirementAttachment[];
  createdAt: string;
}

export interface RequirementAttachment {
  fileName: string;
  fileUrl: string;
  fileSize: number;
  category?: string;
  remark?: string | null;
  recordType?: 'TEXT';
  recordText?: string | null;
  uploaderId?: string;
  uploaderName?: string;
  mimetype?: string;
  responseId?: string;
  stage?: RequirementResponseStage;
  action?: RequirementResponseAction;
  createdAt?: string;
}

export type RequirementAttachmentInput = Pick<
  RequirementAttachment,
  'fileName' | 'fileUrl' | 'fileSize' | 'mimetype' | 'remark'
>;

export interface RequirementComment {
  id: string;
  requirementId: string;
  authorId: string;
  author: Pick<User, 'id' | 'name' | 'avatar' | 'avatarColor'>;
  parentCommentId?: string | null;
  replyToUserId?: string | null;
  parentComment?: Pick<RequirementComment, 'id' | 'authorId' | 'content'> & {
    author?: Pick<User, 'id' | 'name' | 'avatar' | 'avatarColor'>;
  };
  content: string;
  attachments?: RequirementAttachment[];
  mentionedUserIds?: string[];
  createdAt: string;
}

export interface RequirementEvent {
  id: string;
  requirementId: string;
  eventType: RequirementEventType;
  phase?: RequirementResponseStage | null;
  phaseName?: string | null;
  operatorId?: string | null;
  operator?: Pick<User, 'id' | 'name' | 'avatar' | 'avatarColor'> | null;
  metadata?: Record<string, any> | null;
  createdAt: string;
}

export interface Requirement {
  id: string;
  title: string;
  description?: string;
  customerId?: string;
  customerName: string;
  /** 关联客户的编码（由 customer 关联带出，可空；缺失时前端回退展示 customerName） */
  customerCode?: string | null;
  deviceName: string;
  /** 关联设备型号库 ID（可空；库外手输时为空，仅有文本 deviceName） */
  deviceModelId?: string | null;
  deviceType?: DeviceType;
  typeConfigId: string;
  typeConfig?: { id: string; name: string; label: string; color: string };
  status: RequirementStatus;
  applicantId: string;
  applicant: Pick<User, 'id' | 'name'>;
  assigneeIds?: string[];
  assignees?: Pick<User, 'id' | 'name'>[];
  testerIds?: string[];
  testers?: Pick<User, 'id' | 'name'>[];
  acceptorIds?: string[];
  acceptors?: Pick<User, 'id' | 'name'>[];
  projectId: string;
  project?: { id: string; name: string };
  completionCycle: number;
  adjustedCycle?: number;
  startDate?: string;
  endDate?: string;
  /** 研发/测试/验收阶段持续工作日 */
  devDurationDays?: number;
  testDurationDays?: number;
  acceptanceDurationDays?: number;
  devDeadline?: string;
  testDeadline?: string;
  acceptanceDeadline?: string;
  /** 研发/测试阶段实际完成时间（验收实际完成时间复用 completedAt） */
  devCompletedAt?: string;
  testCompletedAt?: string;
  /** 派单时间（进入 PENDING_RESPONSE 时记录） */
  assignedAt?: string;
  /** 研发/测试/验收接受时间 */
  devAcceptedAt?: string;
  testAcceptedAt?: string;
  acceptanceAcceptedAt?: string;
  /** 最近一次拒绝原因 */
  rejectionReason?: string | null;
  /** 暂停前状态 */
  pausedFromStatus?: RequirementStatus | null;
  /** 暂停开始时间 */
  pausedAt?: string | null;
  deadline?: string;
  completedAt?: string;
  attachments?: RequirementAttachment[];
  completionNotifyUserIds?: string[];
  completionNotifyUsers?: Pick<
    User,
    'id' | 'name' | 'avatar' | 'avatarColor'
  >[];
  responses?: RequirementResponse[];
  comments?: RequirementComment[];
  projects?: Pick<Project, 'id' | 'name' | 'status'>[];
  createdAt: string;
  updatedAt: string;
}

export interface CreateRequirementRequest {
  title: string;
  description?: string;
  customerName?: string;
  deviceName: string;
  typeConfigId: string;
  completionCycle?: number;
  startDate?: string;
  /** 结束日期由后端按阶段天数自动计算，前端无需传 */
  endDate?: string;
  /** 研发/测试/验收阶段持续工作日（创建时必填） */
  devDurationDays?: number;
  testDurationDays?: number;
  acceptanceDurationDays?: number;
  projectId: string;
  assigneeIds?: string[];
  testerIds?: string[];
  acceptorIds?: string[];
  completionNotifyUserIds?: string[];
  customerId?: string;
  deviceModelId?: string;
  relatedProjectId?: string;
  reinitiateDraft?: boolean;
}

export interface RespondRequirementRequest {
  type: ResponseType;
  comment?: string;
  extendDays?: number;
  attachments?: RequirementAttachmentInput[];
}

export interface AdvanceRequirementRequest {
  comment?: string;
}

export interface SaveDevRecordRequest {
  recordText?: string;
}

export interface StageDeliverable {
  id: string;
  projectStageId: string;
  fileName: string;
  fileUrl: string;
  fileSize: number;
  fileCategory: string;
  remark?: string | null;
  metadata?: Record<string, any> | null;
  uploader: Pick<User, 'id' | 'name'>;
  createdAt: string;
}

// ==================== 阶段续期 ====================

export enum ExtensionRequestStatus {
  PENDING = 'PENDING',
  APPROVED = 'APPROVED',
  REJECTED = 'REJECTED',
  CLOSED = 'CLOSED',
}

export interface StageExtensionRequest {
  id: string;
  projectStageId: string;
  projectId: string;
  requesterId: string;
  requestDays: number;
  reason: string;
  status: ExtensionRequestStatus;
  approverId?: string;
  approvedAt?: string;
  rejectReason?: string;
  createdAt: string;
  updatedAt: string;
  requester?: Pick<User, 'id' | 'name'>;
  approver?: Pick<User, 'id' | 'name'>;
  projectStage?: { stageName: string };
  project?: { name: string };
}

export enum NotificationType {
  TASK_ASSIGNED = 'TASK_ASSIGNED',
  TASK_OVERDUE_WARNING = 'TASK_OVERDUE_WARNING',
  TASK_OVERDUE = 'TASK_OVERDUE',
  STAGE_ADVANCED = 'STAGE_ADVANCED',
  PROJECT_COMPLETED = 'PROJECT_COMPLETED',
  STAGE_OVERDUE_WARNING = 'STAGE_OVERDUE_WARNING',
  STAGE_OVERDUE = 'STAGE_OVERDUE',
  TASK_COMMENT = 'TASK_COMMENT',
  DAILY_SUMMARY = 'DAILY_SUMMARY',
  REQ_ASSIGNED = 'REQ_ASSIGNED',
  REQ_RESPONSE = 'REQ_RESPONSE',
  REQ_REJECTED = 'REQ_REJECTED',
  REQ_COMMENTED = 'REQ_COMMENTED',
  REQ_MENTIONED = 'REQ_MENTIONED',
  REQ_COMPLETED = 'REQ_COMPLETED',
  REQ_OVERDUE_WARNING = 'REQ_OVERDUE_WARNING',
  REQ_OVERDUE = 'REQ_OVERDUE',
  GATE_PENDING = 'GATE_PENDING',
  GATE_APPROVED = 'GATE_APPROVED',
  GATE_REJECTED = 'GATE_REJECTED',
  PRODUCTION_READY = 'PRODUCTION_READY',
  STAGE_EXTENSION_REQUESTED = 'STAGE_EXTENSION_REQUESTED',
  STAGE_EXTENSION_APPROVED = 'STAGE_EXTENSION_APPROVED',
  STAGE_EXTENSION_REJECTED = 'STAGE_EXTENSION_REJECTED',
  ANNOUNCEMENT_PUBLISHED = 'ANNOUNCEMENT_PUBLISHED',
  APPROVAL_ASSIGNED = 'APPROVAL_ASSIGNED',
  APPROVAL_APPROVED = 'APPROVAL_APPROVED',
  APPROVAL_REJECTED = 'APPROVAL_REJECTED',
  APPROVAL_WITHDRAWN = 'APPROVAL_WITHDRAWN',
  APPROVAL_CC = 'APPROVAL_CC',
  REPORT_SUBMITTED = 'REPORT_SUBMITTED',
  REPORT_APPROVED = 'REPORT_APPROVED',
  REPORT_REJECTED = 'REPORT_REJECTED',
  REPORT_WEEKLY_REMINDER = 'REPORT_WEEKLY_REMINDER',
  REPORT_MONTHLY_REMINDER = 'REPORT_MONTHLY_REMINDER',
  DISCUSSION_REPLIED = 'DISCUSSION_REPLIED',
  DISCUSSION_MENTIONED = 'DISCUSSION_MENTIONED',
  ASSET_CHECKOUT_NOTICE = 'ASSET_CHECKOUT_NOTICE',
  CUSTOMER_OWNER_ASSIGNED = 'CUSTOMER_OWNER_ASSIGNED',
  CUSTOMER_FOLLOWUP_CREATED = 'CUSTOMER_FOLLOWUP_CREATED',
  CUSTOMER_FOLLOWUP_DUE = 'CUSTOMER_FOLLOWUP_DUE',
  CUSTOMER_SAMPLE_FOLLOWUP = 'CUSTOMER_SAMPLE_FOLLOWUP',
  CUSTOMER_POOLED = 'CUSTOMER_POOLED',
  REQ_ADVANCED = 'REQ_ADVANCED',
  REQ_CLOSED = 'REQ_CLOSED',
  REQ_PAUSED = 'REQ_PAUSED',
  REQ_RESUMED = 'REQ_RESUMED',
  ANNOUNCEMENT_REVIEW_PENDING = 'ANNOUNCEMENT_REVIEW_PENDING',
  FEEDBACK_SUBMITTED = 'FEEDBACK_SUBMITTED',
  FEEDBACK_REPLIED = 'FEEDBACK_REPLIED',
  FEEDBACK_FOLLOWUP = 'FEEDBACK_FOLLOWUP',
  ISSUE_SUBMITTED = 'ISSUE_SUBMITTED',
  ISSUE_CLAIMED = 'ISSUE_CLAIMED',
  ISSUE_REJECTED = 'ISSUE_REJECTED',
  ISSUE_ASSIGNED = 'ISSUE_ASSIGNED',
  ISSUE_RESOLVED = 'ISSUE_RESOLVED',
  ISSUE_CLOSED = 'ISSUE_CLOSED',
  ISSUE_OVERDUE = 'ISSUE_OVERDUE',
  URGE = 'URGE',
}

// ==================== 员工心声（Feedback） ====================

/** 员工心声分类 */
export enum FeedbackCategory {
  SUGGESTION = 'SUGGESTION',
  COMPLAINT = 'COMPLAINT',
  CONSULTATION = 'CONSULTATION',
  OTHER = 'OTHER',
}

/** 员工心声状态 */
export enum FeedbackStatus {
  PENDING = 'PENDING',
  IN_PROGRESS = 'IN_PROGRESS',
  REPLIED = 'REPLIED',
  CLOSED = 'CLOSED',
  WITHDRAWN = 'WITHDRAWN',
}

/** 心声附件（上传后存入 attachments 数组） */
export interface FeedbackAttachment {
  fileName: string;
  fileUrl: string;
  fileSize: number;
  mimetype?: string | null;
  /** 由后端写入 */
  uploaderId?: string;
  createdAt?: string;
}

/** 用户简要信息（提交人 / 回复作者） */
export interface FeedbackUserBrief {
  id: string;
  name: string;
  avatar?: string | null;
  avatarColor?: string | null;
}

/** 心声回复（多轮对话的一条） */
export interface FeedbackReplyDTO {
  id: string;
  feedbackId: string;
  authorId: string;
  /** SUBMITTER = 员工回复，MANAGER = 处理人回复 */
  authorRole: 'SUBMITTER' | 'MANAGER';
  content: string;
  attachments: FeedbackAttachment[];
  author?: FeedbackUserBrief;
  createdAt: string;
}

/** 员工心声 */
export interface FeedbackDTO {
  id: string;
  title: string;
  content: string;
  category: FeedbackCategory;
  status: FeedbackStatus;
  attachments: FeedbackAttachment[];
  submitterId: string;
  /** 本条心声的接收人快照（创建时取自全局配置） */
  receiverIds: string[];
  submitter?: FeedbackUserBrief;
  /** 列表接口返回回复条数 */
  _count?: { replies: number };
  /** 详情接口返回完整对话 */
  replies?: FeedbackReplyDTO[];
  lastReplyAt?: string | null;
  closedAt?: string | null;
  closedById?: string | null;
  createdAt: string;
  updatedAt: string;
}

/** 反馈接收人配置（/api/feedback/config 返回） */
export interface FeedbackConfigDTO {
  receiverIds: string[];
  receivers: FeedbackUserBrief[];
  updatedAt?: string;
  updatedBy?: string | null;
}

// ==================== 审计日志 ====================

export interface AuditLog {
  id: string;
  operatorId?: string | null;
  operator?: { id: string; name: string } | null;
  username?: string | null;
  deptName?: string | null;
  ip?: string | null;
  userAgent?: string | null;
  location?: string | null;
  module?: string | null;
  targetType?: string | null;
  targetId?: string | null;
  action: string;
  businessType?: string | null;
  description?: string | null;
  requestUrl?: string | null;
  requestMethod?: string | null;
  requestParam?: unknown;
  responseBody?: unknown;
  beforeData?: unknown;
  afterData?: unknown;
  diffPatch?: Array<{ op: string; path: string; value?: unknown }> | null;
  status?: number | null;
  errorMsg?: string | null;
  costTime?: number | null;
  createdAt: string;
}

export interface AuditLogQuery {
  current?: number;
  page?: number;
  pageSize?: number;
  module?: string;
  businessType?: string;
  targetType?: string;
  targetId?: string;
  operatorId?: string;
  startDate?: string;
  endDate?: string;
  keyword?: string;
}

// ==================== 问题反馈（Issue） ====================

export enum IssueStatus {
  PENDING_CLAIM = 'PENDING_CLAIM',
  REJECTED = 'REJECTED',
  IN_PROGRESS = 'IN_PROGRESS',
  ON_HOLD = 'ON_HOLD',
  RESOLVED = 'RESOLVED',
  CLOSED = 'CLOSED',
}

export enum IssueUrgency {
  NORMAL = 'NORMAL',
  URGENT = 'URGENT',
  CRITICAL = 'CRITICAL',
}

export enum IssueLogAction {
  SUBMIT = 'SUBMIT',
  CLAIM = 'CLAIM',
  REJECT = 'REJECT',
  TRANSFER = 'TRANSFER',
  COMMENT = 'COMMENT',
  RESOLVE = 'RESOLVE',
  HOLD = 'HOLD',
  RESUME = 'RESUME',
  CLOSE = 'CLOSE',
}

export interface IssueUser {
  id: string;
  name: string;
  avatar?: string | null;
  avatarColor?: string | null;
}

export interface IssueAttachment {
  fileName: string;
  fileUrl: string;
  fileSize: number;
  mimetype?: string | null;
}

export interface IssueLog {
  id: string;
  issueId: string;
  action: IssueLogAction;
  operatorId: string;
  operator?: IssueUser;
  content?: string | null;
  attachments?: IssueAttachment[];
  createdAt: string;
}

export interface IssueViewerDepartment {
  id: string;
  issueId: string;
  departmentId: string;
  department?: { id: string; name: string };
}

export interface IssueViewerUser {
  id: string;
  issueId: string;
  userId: string;
  user?: IssueUser;
}

export interface Issue {
  id: string;
  issueNo: string;
  title: string;
  category: string;
  urgency: IssueUrgency;
  departmentId: string;
  department?: { id: string; name: string };
  description: string;
  suggestion?: string | null;
  images: IssueAttachment[];
  attachments: IssueAttachment[];
  submitterId: string;
  submitter?: IssueUser;
  status: IssueStatus;
  assigneeId?: string | null;
  assignee?: IssueUser | null;
  viewerDepartments?: IssueViewerDepartment[];
  viewerUsers?: IssueViewerUser[];
  resolvedAt?: string | null;
  resolution?: string | null;
  confirmed: boolean;
  closedAt?: string | null;
  createdAt: string;
  updatedAt: string;
  logs?: IssueLog[];
  _count?: { logs: number };
  canClaim?: boolean;
  canHandle?: boolean;
  canEdit?: boolean;
  canManageViewers?: boolean;
  canDelete?: boolean;
}

export interface CreateIssueRequest {
  title: string;
  category: string;
  urgency?: IssueUrgency;
  departmentId: string;
  description: string;
  suggestion?: string;
  images?: IssueAttachment[];
  attachments?: IssueAttachment[];
  viewerDepartmentIds?: string[];
  viewerUserIds?: string[];
}

export type UpdateIssueRequest = Partial<CreateIssueRequest>;

export interface IssueTabCounts {
  all: number;
  pending: number;
  inProgress: number;
  resolved: number;
  mine: number;
  assigned: number;
}
